import { toast } from 'sonner';
import { useForm } from 'react-hook-form';
import { FirebaseError } from 'firebase/app';
import { captureException } from '@sentry/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useSearchParams, useLocation, Navigate } from 'react-router';
import React, { useCallback, useState, useRef, useEffect } from 'react';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Typography, Button, TextField, Box, Stack, InputAdornment } from '@mui/material';
import { PhoneAuthProvider, PhoneMultiFactorGenerator, RecaptchaVerifier, multiFactor } from 'firebase/auth';
import { MFA_SMS_COOLDOWN_MS } from '@shared/auth/constants';

import { auth } from '../../../../firebase-config';
import { useAuth } from '../../../../hooks/useAuth';
import { NavigateWithRedirect } from '../../../../Router/NavigateWithRedirect';
import { MFA_FACTOR_IDS } from '../../constants';
import { AuthenticationModal } from '../../components';
import { DEFAULT_OTP_INPUT_FIELD_VALUE, parseOTPInputValue } from '../../components/OTPInput';
import { SetupPage } from '../SetupPage';
import { addSuccessful2FAToActivityLog } from '../addSuccessful2FAToActivityLog';
import { smsSchema, normaliseAusMobile } from './smsSchema';
import { VerifyCodeSection } from './VerifyCodeSection';
import { mfaColors, mfaPrimaryButtonStyles, mfaInputStyles, mfaErrorStyles, mfaSecondaryButtonStyles } from '../styles';
import { isPatient } from '../../../../utils';
import { isUserEnrolledInMFAMethod } from '../../utils';

/**
 * Handles Firebase-specific errors during SMS setup
 * @param {Object} params
 * @param {() => void} params.handleRequiresRecentLogin
 * @param {import('react-hook-form').UseFormSetError<SMSFormType>} params.setError
 * @param {FirebaseError} params.error
 */
const handleFirebaseError = ({ handleRequiresRecentLogin, setError, error }) => {
  const errorHandlers = {
    'auth/invalid-verification-code': () =>
      setError('verificationCode', { message: 'Invalid verification code. Please check and try again.' }),
    'auth/invalid-phone-number': () =>
      setError('phoneNumber', { message: 'Please enter a valid Australian mobile number.' }),
    'auth/requires-recent-login': handleRequiresRecentLogin,
    'auth/too-many-requests': () =>
      setError('root', { message: 'Too many attempts. Please wait a few minutes before trying again.' }),
  };

  const handler = errorHandlers[error.code];
  if (handler) {
    handler();
    return;
  }

  setError('root', { message: 'Something went wrong. Please try again.' });
};

const CountryCodeAdornment = () => (
  <InputAdornment position="start">
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        pr: 1.5,
        mr: 1,
        borderRight: `1px solid ${mfaColors.border}`,
      }}
    >
      <Typography component="span" sx={{ fontSize: '0.95rem', fontWeight: 500, color: mfaColors.text }}>
        +61
      </Typography>
    </Box>
  </InputAdornment>
);

const PhoneNumberInput = ({ form, disabled }) => {
  const { errors } = form.formState;

  return (
    <Box>
      <Typography variant="body2" sx={{ color: mfaColors.text, fontWeight: 500, mb: 1 }}>
        Mobile number
      </Typography>
      <TextField
        fullWidth
        disabled={disabled}
        autoComplete="tel"
        placeholder="400 000 000"
        {...form.register('phoneNumber')}
        error={!!errors.phoneNumber}
        helperText={errors.phoneNumber?.message || 'Australian mobile numbers only'}
        slotProps={{ input: { startAdornment: <CountryCodeAdornment /> } }}
        sx={{
          ...mfaInputStyles,
          '& .MuiOutlinedInput-root.Mui-disabled': { backgroundColor: mfaColors.background },
        }}
      />
    </Box>
  );
};

const SendCodeActions = ({ show, isSubmitting, user, canSelectMethod, onBack }) => {
  if (!show) {
    return null;
  }

  return (
    <Stack direction="row" spacing={2} justifyContent="center">
      {canSelectMethod ? (
        <Button onClick={onBack} disabled={isSubmitting} sx={{ flex: 1, ...mfaSecondaryButtonStyles }}>
          Back
        </Button>
      ) : (
        <SetupPage.CancelButton disabled={isSubmitting} user={user} sx={{ flex: 1 }} />
      )}
      <Button type="submit" disabled={isSubmitting} sx={{ flex: 1, ...mfaPrimaryButtonStyles }}>
        {isSubmitting ? 'Sending...' : 'Send Code'}
      </Button>
    </Stack>
  );
};

const FormError = ({ message }) => {
  if (!message) {
    return null;
  }

  return (
    <Box sx={mfaErrorStyles}>
      <ErrorOutlineIcon sx={{ color: mfaColors.error, fontSize: 18, mt: 0.25 }} />
      <Typography variant="body2" sx={{ color: mfaColors.error }}>
        {message}
      </Typography>
    </Box>
  );
};

export const SMSSetupPage = () => {
  const { user, userType } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const recaptchaVerifierRef = useRef(/** @type {RecaptchaVerifier | null} */ (null));
  const sendInProgressRef = useRef(false);

  const [lastSent, setLastSent] = useState(0);
  const [verificationId, setVerificationId] = useState('');
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [requiresReauthentication, setRequiresReauthentication] = useState(false);

  const enrolledMultiFactorUser = user ? multiFactor(user) : null;
  const userAlreadyHasSMSMFA = enrolledMultiFactorUser
    ? isUserEnrolledInMFAMethod({
        factorId: MFA_FACTOR_IDS.PHONE,
        multiFactorUser: enrolledMultiFactorUser,
      })
    : false;
  const canSelectMethod = !!user && isPatient(userType) && !userAlreadyHasSMSMFA;

  /** @type {import('react-hook-form').UseFormReturn<SMSFormType>} */
  const form = useForm({
    resolver: zodResolver(smsSchema),
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: {
      phoneNumber: searchParams.get('phoneNumber') || '',
      verificationCode: DEFAULT_OTP_INPUT_FIELD_VALUE,
    },
  });

  const { setError, getValues, handleSubmit, formState } = form;
  const { isSubmitting, errors } = formState;

  const handleRequiresRecentLogin = useCallback(() => setRequiresReauthentication(true), []);

  useEffect(
    () => () => {
      if (recaptchaVerifierRef.current !== null) {
        recaptchaVerifierRef.current.clear();
        recaptchaVerifierRef.current = null;
      }
    },
    [],
  );

  const handleSendVerificationCode = useCallback(
    async (data) => {
      if (!user) {
        return;
      }

      if (sendInProgressRef.current) {
        return;
      }

      if (lastSent > 0 && Date.now() - lastSent < MFA_SMS_COOLDOWN_MS) {
        return;
      }

      try {
        sendInProgressRef.current = true;
        setIsSendingCode(true);
        const activeMultiFactorUser = multiFactor(user);
        const session = await activeMultiFactorUser.getSession();

        if (!recaptchaVerifierRef.current) {
          recaptchaVerifierRef.current = new RecaptchaVerifier(auth, 'recaptcha-container', { size: 'invisible' });
          await recaptchaVerifierRef.current.verify();
        }

        const normalisedPhone = normaliseAusMobile(data.phoneNumber);
        const phoneAuthProvider = new PhoneAuthProvider(auth);
        const newVerificationId = await phoneAuthProvider.verifyPhoneNumber(
          { phoneNumber: normalisedPhone, session },
          recaptchaVerifierRef.current,
        );

        setVerificationId(newVerificationId);
        setLastSent(Date.now());
        toast.success('Verification code sent to your phone');
      } catch (error) {
        if (error instanceof FirebaseError) {
          handleFirebaseError({ error, setError, handleRequiresRecentLogin });
          return;
        }

        const sentryIssueId = captureException(error, { level: 'error', extra: { phoneNumber: data.phoneNumber } });
        setError('root', { type: 'sentry', message: `Failed to send code. Please try again. (${sentryIssueId})` });
      } finally {
        setIsSendingCode(false);
        sendInProgressRef.current = false;
      }
    },
    [user, setError, handleRequiresRecentLogin, lastSent],
  );

  const handleVerifyCode = useCallback(
    async (data) => {
      if (!verificationId || !user || !data.verificationCode) {
        return;
      }

      try {
        const parsedVerificationCode = parseOTPInputValue(data.verificationCode);
        const credential = PhoneAuthProvider.credential(verificationId, parsedVerificationCode);
        const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(credential);
        const normalisedPhone = normaliseAusMobile(getValues().phoneNumber);

        await multiFactor(user).enroll(multiFactorAssertion, normalisedPhone);
        await addSuccessful2FAToActivityLog({ userId: user.uid, factorId: MFA_FACTOR_IDS.PHONE });

        const urlSearchParams = new URLSearchParams(window.location.search);
        const redirect = urlSearchParams.get('redirect');

        if (redirect) {
          urlSearchParams.delete('redirect');
          navigate(`${redirect}?${urlSearchParams.toString()}`);
        } else {
          navigate('/dashboard');
        }
      } catch (error) {
        if (error instanceof FirebaseError) {
          handleFirebaseError({ error, setError, handleRequiresRecentLogin });
          return;
        }

        const sentryIssueId = captureException(error, {
          level: 'error',
          extra: { phoneNumber: data.phoneNumber, verificationCode: data.verificationCode },
        });
        setError('root', { type: 'sentry', message: `Verification failed. Please try again. (${sentryIssueId})` });
      }
    },
    [user, verificationId, setError, getValues, handleRequiresRecentLogin, navigate],
  );

  const onSubmit = useCallback(
    async (data) => {
      if (!verificationId) {
        await handleSendVerificationCode(data);
        return;
      }
      await handleVerifyCode(data);
    },
    [verificationId, handleSendVerificationCode, handleVerifyCode],
  );

  const handleReauthenticationSuccess = useCallback(async () => {
    await auth.currentUser?.reload();
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  if (!user) {
    return <NavigateWithRedirect to="/login" />;
  }

  if (userAlreadyHasSMSMFA || !isPatient(userType)) {
    return <Navigate replace to="/dashboard" />;
  }

  const isAwaitingCode = !verificationId;

  return (
    <SetupPage
      form={form}
      method={MFA_FACTOR_IDS.PHONE}
      title="Set Up SMS Verification"
      subtitle="We'll send a verification code to your mobile phone each time you sign in."
      onSubmit={onSubmit}
      containerProps={{ style: { maxWidth: '440px' } }}
    >
      <div id="recaptcha-container" />

      <Stack spacing={3} sx={{ width: '100%' }}>
        <PhoneNumberInput form={form} disabled={!isAwaitingCode} />
        <SendCodeActions
          show={isAwaitingCode}
          isSubmitting={isSubmitting}
          user={user}
          canSelectMethod={canSelectMethod}
          onBack={() => navigate('/mfa-setup/totp', { state: { ...location.state, skipInfoPage: true } })}
        />
        <VerifyCodeSection
          verificationId={verificationId}
          onSubmit={handleVerifyCode}
          lastSent={lastSent}
          isSendingCode={isSendingCode}
          handleSendVerificationCode={handleSendVerificationCode}
        />
        <FormError message={isAwaitingCode ? errors.root?.message : null} />
      </Stack>

      <AuthenticationModal
        nonDismissable
        modalOpen={requiresReauthentication}
        setModalOpen={setRequiresReauthentication}
        mode="reauthenticate"
        onSuccess={handleReauthenticationSuccess}
      />
    </SetupPage>
  );
};
